import styles from "./CheckoutDirectForm.module.scss";
import { useState } from "react";
import {
  Message,
  Button,
  ButtonGroup,
  Empty,
  Text,
  useAuth,
  Loader,
} from "@eventsquare/uikit";
import {
  Form,
  FormCheckbox,
  FormGroup,
  FormInput,
  FormLabel,
  FormSelect,
  FormTextArea,
  FormFieldset,
} from "@eventsquare/uikit/forms";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RiCheckboxCircleLine, RiShoppingCartLine } from "@remixicon/react";

import { ChannelCart } from "@type/channelCart";

import { formatCurrency, hasSeatmapTypes } from "@lib/helpers";
import { Api } from "@lib/api";
import { removeCookie } from "@lib/cookies";
import { Currency } from "@type/currency";
import { usePrint } from "@/hooks/usePrint";
import { ButtonStack } from "@components/ButtonStack/ButtonStack";

interface CheckoutDirectFormProps {
  cart: ChannelCart;
  setShowSwitch: (show: boolean) => void;
  languages: { value: string; label: string }[];
}

interface FormDirectValues {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  language: "nl" | "fr" | "en";
  lock_places: boolean;
  note: string;
}
//TODO : get proper typing for Order
interface Order {
  data: {
    pockets: {
      url: string;
      pocketid: string;
    }[];
  };
}

export const CheckoutDirectForm = (props: CheckoutDirectFormProps) => {
  const { cart, setShowSwitch, languages } = props;

  const navigate = useNavigate();
  const { t } = useTranslation(["components_checkout_direct_form", "common"]);
  const { account } = useAuth();

  const [step, setStep] = useState<
    "form" | "confirm" | "processing" | "success"
  >("form"); // Should be form by default
  const [order, setOrder] = useState<Order | null>(null);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<string | null>(null);
  const [confirmValues, setConfirmValues] = useState<
    FormDirectValues | undefined
  >(undefined);

  const { hasPrinter, printPocket } = usePrint();

  const initialValues: FormDirectValues = {
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    language: "nl",
    lock_places: false,
    note: "",
  };

  const handleSubmit = (values: FormDirectValues) => {
    setShowSwitch(false);
    setErrors(null);
    setConfirmValues(values);
    setStep("confirm");
  };

  const confirmPayment = async () => {
    setErrors(null);
    setSending(true);

    if (!confirmValues) return;

    const data = {
      invoice: 0,
      redirecturl: "http://www.eventsquare.io",
      testmode: 0,
      customer: {
        firstname: confirmValues.firstname,
        lastname: confirmValues.lastname,
        email: confirmValues.email,
        telephone: confirmValues.phone,
        language: confirmValues.language,
      },
      lock_places: confirmValues.lock_places ? 1 : 0,
      note:
        confirmValues.note.trim() !== ""
          ? confirmValues.note.trim()
          : undefined,
    };

    try {
      const response = await Api.post(`/cart/${cart.cartid}`, data);

      if (response.order) {
        setStep("processing");
        waitForOrderToBeProcessed(response.order.orderid);
        setConfirmValues(undefined);
      }
    } catch (error) {
      setStep("form");
      setShowSwitch(true);
      setErrors("something_went_wrong");
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const waitForOrderToBeProcessed = async (orderId: string) => {
    try {
      const response = await Api.get(`/checkout/${orderId}`);
      if (response.order.action === "processing") {
        setTimeout(() => {
          waitForOrderToBeProcessed(orderId);
        }, 500);
      } else {
        removeCookie();
        setOrder(response.order);
        printTickets(response.order.data.pockets[0].pocketid);
        setStep("success");
      }
    } catch (error) {
      setStep("form");
      setShowSwitch(true);
      setErrors("something_went_wrong");
      console.error(error);
    }
  };

  const printTickets = (pocketId?: string) => {
    if (!hasPrinter || !pocketId) return;
    printPocket(pocketId);
  };

  const downloadTickets = async () => {
    if (!order) return;

    window.open(order.data.pockets[0].url + "/tickets");
  };

  const closeCheckout = async () => {
    await removeCookie();
    navigate("/");
  };

  return (
    <div className={styles.checkoutDirectForm}>
      {step === "form" && (
        <>
          <Text variant="h5">{t("form.title")}</Text>
          <Text variant="p">{t("form.lead")}</Text>
          <Form onSubmit={handleSubmit} initialValues={initialValues}>
            {errors && (
              <Message alert type="error" message={t(`errors.${errors}`)} />
            )}
            <FormFieldset disabled={sending}>
              <FormGroup>
                <FormLabel
                  htmlFor="firstname"
                  helperText={t("common:optional")}
                >
                  {t("form.firstname")}
                </FormLabel>
                <FormInput name="firstname" type="text" />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="lastname" helperText={t("common:optional")}>
                  {t("form.lastname")}
                </FormLabel>
                <FormInput name="lastname" type="text" />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="email" helperText={t("common:optional")}>
                  {t("form.email")}
                </FormLabel>
                <FormInput name="email" type="email" />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="phone" helperText={t("common:optional")}>
                  {t("form.phone")}
                </FormLabel>
                <FormInput name="phone" type="tel" />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="lang">{t("form.language")}</FormLabel>
                <FormSelect
                  placeholder={t("form.lang_placeholder")}
                  name="language"
                  options={languages.sort((a, b) =>
                    a.label < b.label ? -1 : 1,
                  )}
                />
              </FormGroup>
              {hasSeatmapTypes(cart) && (
                <FormGroup>
                  <FormCheckbox
                    name="lock_places"
                    label={t("form.lock_places")}
                  />
                </FormGroup>
              )}
              <FormGroup>
                <FormLabel htmlFor="note" helperText={t("common:optional")}>
                  {t("form.note")}
                </FormLabel>
                <FormTextArea name="note" />
              </FormGroup>
            </FormFieldset>
            <div className={styles.submitButton}>
              <ButtonGroup>
                <Button block type="submit">
                  {t("form.submit")}
                </Button>
              </ButtonGroup>
            </div>
          </Form>
        </>
      )}
      {step === "confirm" && (
        <>
          <Empty
            icon={<RiShoppingCartLine />}
            title={t("confirmation.title")}
            lead={t("confirmation.lead", {
              amount: formatCurrency(
                cart.summary.total,
                account?.currency as Currency,
              ),
            })}
          >
            <ButtonGroup>
              <Button onClick={confirmPayment} type="button">
                {t("confirmation.confirm")}
              </Button>
            </ButtonGroup>
          </Empty>
        </>
      )}
      {step === "processing" && (
        <Empty icon={<Loader size="small" />} title={t("processing")} lead="" />
      )}
      {step === "success" && (
        <>
          <Empty
            icon={<RiCheckboxCircleLine />}
            title={t("success.title")}
            lead=""
          >
            <ButtonStack>
              {!hasPrinter && (
                <Button variant="outline" onClick={downloadTickets}>
                  {t("success.download_tickets")}
                </Button>
              )}
              <Button onClick={closeCheckout}>
                {t("success.finish_checkout")}
              </Button>
              {hasPrinter && (
                <Button
                  variant="outline"
                  onClick={() => printTickets(order?.data.pockets[0].pocketid)}
                >
                  {t("success.print_tickets")}
                </Button>
              )}
            </ButtonStack>
          </Empty>
        </>
      )}
    </div>
  );
};
